/* eslint-disable react-hooks/exhaustive-deps */

import { collection, doc, setDoc, getDocs, query, where, onSnapshot, writeBatch, orderBy } from 'firebase/firestore';
import { db } from './firebase';

const NODES_COLLECTION = 'nodes';

// Debug version of saveNodeToFirestore
export async function saveNodeToFirestore(userId, sessionId, node, parentId = null) {
  // Validate inputs
  if (!userId || !sessionId || !node) {
    console.error('Missing required parameters:', { userId, sessionId, node });
    return;
  }

  // console.log('Attempting to save node:', { sessionId, nodeId: node.id, parentId });

  const nodeData = {
    user_id: userId, // Add user ID
    session_id: sessionId,
    id: node.id,
    parent_id: parentId,
    header: node?.data?.header ?? '',
    content: node?.data?.content ?? '',
    url_sources: node?.data?.urlSources ?? [],
    position: node?.position ?? { x: 0, y: 0 },
    timestamp: new Date().toISOString(), // Add timestamp for debugging
  };

  try {
    // Log the exact document reference we're trying to write to
    const docRef = doc(collection(db, NODES_COLLECTION), node.id.toString());
    // console.log('Writing to document:', docRef.path);

    // Add a listener to verify the write
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        // console.log('Document data:', doc.data());
      } else {
        // console.log('Document does not exist even after write attempt');
      }
    });

    // Attempt the write
    await setDoc(docRef, nodeData);
    
    // Verify the write immediately
    const verifyDoc = await getDocs(query(
      collection(db, NODES_COLLECTION),
      where('id', '==', node.id)
    ));
    
    if (verifyDoc.empty) {
      console.error('Verification failed: Document not found after write');
    } else {
      // console.log('Verification successful:', verifyDoc.docs[0].data());
    }

    // Cleanup listener after 5 seconds
    setTimeout(() => unsubscribe(), 5000);

  } catch (error) {
    console.error('Detailed save error:', {
      error,
      errorMessage: error.message,
      errorCode: error.code,
      nodeId: node.id,
      sessionId,
      userId,
    });
    throw error;
  }
}

// Debug version of saveHierarchyToFirestore
export const saveHierarchyToFirestore = async (userId, sessionId, nodes) => {
  if (!userId || !nodes) {
    console.error('Missing required parameters:', { userId, sessionId, nodes });
    return;
  }
  
  // console.log('Saving hierarchy with sessionID: ', sessionId, ' and userID:', userId);
  
  try {
    // console.log('UserId before writeBatch: ', userId)
    const batch = writeBatch(db);
    nodes.forEach((node) => {
      const nodeRef = doc(db, NODES_COLLECTION, node.id.toString());
      // console.log('Preparing to save node:', node.id, 'Data:', node);

      const nodeData = {
        user_id: userId, // Add user ID
        session_id: sessionId,
        id: node.id,
        parent_id: node.parentNode || null,
        header: node?.data?.originalHeader ?? '',
        content: node?.data?.originalContent ?? '',
        url_sources: node?.data?.urlSources ?? [],
        images: node?.data?.images ?? [],
        position: node?.position ?? { x: 0, y: 0 },
        type: node?.data?.type ?? '',
        timestamp: new Date().toISOString(),
      };

      batch.set(nodeRef, nodeData);
      // console.log('Created batch: ', nodeData)
    });

    // console.log('Committing batch...');
    await batch.commit();
    // console.log('Batch committed successfully');
  } catch (error) {
    console.error('Error saving hierarchy:', error);
    throw error;
  }
};

// Enhanced debug version of fetchHierarchyFromFirestore
export const fetchSearchHistory = async (userId) => {
  if (!userId) {
    console.error('Missing required parameters:', { userId });
    return [];
  }

  try {
    // console.log('Fetching search history for user:', userId);

    const historyQuery = query(
      collection(db, NODES_COLLECTION),
      where('user_id', '==', userId), // Fetch only for the current user
      where('header', '==', 'topic name'),
      orderBy('timestamp', 'desc') // Order by latest searches
    );

    const querySnapshot = await getDocs(historyQuery);

    // Extract the relevant fields (ensure 'header' is returned)
    const history = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      header: doc.data().content || 'Untitled Topic',
      timestamp: doc.data().timestamp || '',
    }));

    // console.log('Fetched search history:', history);
    return history;
  } catch (error) {
    console.error('Error fetching search history:', error);
    throw error;
  }
};

export const fetchHierarchyFromFirestore = async (topicContent, userId) => {
  if (!topicContent || !userId) {
    console.error("Missing required parameters:", { topicContent, userId });
    return [];
  }

  try {
    // console.log(`Fetching hierarchy for topic: "${topicContent}" and user: ${userId}`);

    // Step 1: Fetch the root node (topic node)
    const rootQuery = query(
      collection(db, NODES_COLLECTION),
      where("content", "==", topicContent),
      where("user_id", "==", userId)
    );

    const rootSnapshot = await getDocs(rootQuery);

    if (rootSnapshot.empty) {
      console.warn("No root node found for topic:", topicContent);
      return [];
    }

    // console.log('Root snapshop: ', rootSnapshot.docs[0].data())

    const rootNode = rootSnapshot.docs[0].data();
    const rootId = rootNode.id;

    // console.log("Root node fetched:", rootNode);

    // Step 2: Fetch all child nodes iteratively
    const nodes = [rootNode];
    const fetchChildNodes = async (parentId) => {
      const childQuery = query(
        collection(db, NODES_COLLECTION),
        where("parent_id", "==", parentId),
        where("user_id", "==", userId)
      );

      const childSnapshot = await getDocs(childQuery);
      // console.log('Child snapshot: ', childSnapshot.docs[0])
      const childNodes = childSnapshot.docs.map((doc) => doc.data());
      // console.log('Child Nodes mapped: ', childNodes)

      // console.log(`Fetched ${childNodes.length} child nodes for parent ID: ${parentId}`, childNodes);

      nodes.push(...childNodes);

      // Recursively fetch children for each child node
      await Promise.all(childNodes.map((node) => fetchChildNodes(node.id)));
    };

    await fetchChildNodes(rootId);

    // console.log("Complete hierarchy fetched:", nodes);
    return nodes;
  } catch (error) {
    console.error("Error fetching hierarchy:", error);
    throw error;
  }
};

// Helper function to check Firestore permissions
export async function checkFirestorePermissions() {
  try {
    // Try to write a test document
    const testDoc = doc(collection(db, NODES_COLLECTION), 'test-permissions');
    await setDoc(testDoc, { test: true }, { merge: true });
    // console.log('Write permission verified');

    // Clean up test document
    await setDoc(testDoc, { test: false }, { merge: true });
    return true;

  } catch (error) {
    console.error('Permission check failed:', {
      error,
      errorMessage: error.message,
      errorCode: error.code,
    });
    return false;
  }
}